import { AmountCode, EmployeePayType, EmployeePayTypeTitle } from 'typings/enums';
import { action, observable, computed, makeObservable } from 'mobx';

const convertNonBudgetTccFields = {
    [EmployeePayTypeTitle.LastPaidSti]: EmployeePayType.LastPaidSti,
    [EmployeePayTypeTitle.OtherCash]: EmployeePayType.OtherCash,
    [EmployeePayTypeTitle.Commissions]: EmployeePayType.Commissions
};

export class TeamPageViews {
    @observable private _tcc: boolean;

    @observable private _excludedTccFields: Set<string>;

    @observable private _useCompSummaryFields: boolean;

    @observable private _excludedCompSummaryFields: Set<string>;

    @observable private _lockViewForManager: boolean;

    @observable private _showIncreaseRecommendation: boolean;

    @observable private _managerIncreaseDecision: boolean;

    @observable private _managerIncreasePercentageUnder: number;

    @observable private _managerIncreasePercentageOver: number;

    @observable private _preFillDecision: boolean;

    @observable private _preFillDecisionFields: string[];

    @observable private _multiCountry: boolean;

    @observable private _hideIneligibleEmployees: boolean;

    @observable private _disableCompMemoDonwload: boolean;

    constructor(teamPageViews: ITeamPageViews) {
        makeObservable(this);
        this._tcc = teamPageViews.tcc;
        this._excludedTccFields = new Set(teamPageViews.excludedTccFields);

        this._useCompSummaryFields = teamPageViews.shouldDisplayAllCompSummaryFields;
        this._excludedCompSummaryFields = new Set<string>(teamPageViews.excludedCompSummaryFields);

        this._lockViewForManager = teamPageViews.lockViewForManager;
        this._showIncreaseRecommendation = teamPageViews.increaseRecommendation;
        this._managerIncreaseDecision = teamPageViews.managerIncreaseDecision;
        this._managerIncreasePercentageUnder = teamPageViews.managerIncreasePercentageUnder;
        this._managerIncreasePercentageOver = teamPageViews.managerIncreasePercentageOver;
        this._preFillDecision = teamPageViews.preFillDecision;
        this._preFillDecisionFields = teamPageViews.preFillDecisionFields;
        this._multiCountry = teamPageViews.multiCountry;
        this._hideIneligibleEmployees = teamPageViews.hideIneligibleEmployees;
        this._disableCompMemoDonwload = teamPageViews.disableCompMemoDownload;
    }

    @action
    doesAmountUseTccField(amountCode: AmountCode): boolean {
        if (amountCode === AmountCode.Stock) {
            return false;
        }

        return this.useTccField(amountCode);
    }

    @action
    isAmountUsedInCompSummary(amountCode: AmountCode): boolean {
        return this.usedInCompSummary(amountCode);
    }
    
    @computed
    get multiCountry(): boolean {
        return this._multiCountry;
    }
    
    @computed
    get doesLastPaidStiUseTccField(): boolean {
        return this.useTccField(EmployeePayType.LastPaidSti);
    }

    @computed
    get doesCommissionsUseTccField(): boolean {
        return this.useTccField(EmployeePayType.Commissions);
    }

    @computed
    get doesOtherCashUseTccField(): boolean {
        return this.useTccField(EmployeePayType.OtherCash);
    }

    @computed
    get isLastPaidStiUsedInCompSummary(): boolean {
        return this.usedInCompSummary(EmployeePayType.LastPaidSti);
    }

    @computed
    get isCommissionsUsedInCompSummary(): boolean {
        return this.usedInCompSummary(EmployeePayType.Commissions);
    }

    @computed
    get isOtherCashUsedInCompSummary(): boolean {
        return this.usedInCompSummary(EmployeePayType.OtherCash);
    }

    @computed
    get tcc(): boolean {
        return this._tcc;
    }

    @computed
    get lockViewForManager(): boolean {
        return this._lockViewForManager;
    }

    @computed
    get allExcludedFields(): Set<string> {
        return this._excludedTccFields;
    }

    @computed
    get allNonBudgetFieldNamesThatAffectTcc(): string[] {
        const fields = [];
        if (this.doesLastPaidStiUseTccField) {
            fields.push(EmployeePayTypeTitle.LastPaidSti);
        }
        if (this.doesCommissionsUseTccField) {
            fields.push(EmployeePayTypeTitle.Commissions);
        }
        if (this.doesOtherCashUseTccField) {
            fields.push(EmployeePayTypeTitle.OtherCash);
        }
        return fields;
    }

    @computed
    get allNonBudgetFieldAmountsThatAffectTcc(): string[] {
        const fields = [];
        if (this.doesLastPaidStiUseTccField) {
            fields.push(EmployeePayType.LastPaidSti);
        }
        if (this.doesCommissionsUseTccField) {
            fields.push(EmployeePayType.Commissions);
        }
        if (this.doesOtherCashUseTccField) {
            fields.push(EmployeePayType.OtherCash);
        }
        return fields;
    }

    convertNonBudgetToAmountCode(value: string): EmployeePayType {
        return convertNonBudgetTccFields[value as EmployeePayTypeTitle];
    }

    @action
    setTcc(value: boolean): void {
        this._tcc = value;
    }

    @action
    addFieldToExclude(value: string): void {
        this._excludedTccFields.add(value);
    }

    @action
    removeFieldToExclude(value: string): void {
        this._excludedTccFields.delete(value);
    }

    @action
    setDisableCompMemoDownload(value: boolean): void {
        this._disableCompMemoDonwload = value;
    }

    @computed
    get showIncreaseRecommendation(): boolean {
        return this._showIncreaseRecommendation;
    }

    @computed
    get disableCompMemoDownload(): boolean {
        return this._disableCompMemoDonwload;
    }

    @computed
    get managerIncreaseDecision(): boolean {
        return this._managerIncreaseDecision;
    }

    @computed
    get managerIncreasePercentageUnder(): number {
        return this._managerIncreasePercentageUnder;
    }

    @computed
    get managerIncreasePercentageOver(): number {
        return this._managerIncreasePercentageOver;
    }

    @computed
    get preFillDecision(): boolean {
        return this._preFillDecision;
    }

    @computed
    get preFillDecisionFields(): string[] {
        return this._preFillDecisionFields;
    }

    @computed
    get isHideIneligibleEmployeesChecked(): boolean {
        return this._hideIneligibleEmployees;
    }

    @action
    setUseCompSummaryFields(value: boolean): void {
        this._useCompSummaryFields = value;
    }

    @action
    setHideIneligibleEmployees(value: boolean): void {
        this._hideIneligibleEmployees = value;
    }

    @action
    setExcludedCompSummaryFields(fields: string[]): void {
        this._excludedCompSummaryFields = new Set<string>(fields);
    }

    @action
    setShowIncreaseRecommendation(value: boolean): void {
        this._showIncreaseRecommendation = value;
    }

    @action
    setManagerIncreaseDecision(value: boolean): void {
        this._managerIncreaseDecision = value;
    }

    @action
    setManagerIncreasePercentageUnder(value: number): void {
        this._managerIncreasePercentageUnder = value;
    }

    @action
    setManagerIncreasePercentageOver(value: number): void {
        this._managerIncreasePercentageOver = value;
    }

    @action
    setPreFillDecision(value: boolean): void {
        this._preFillDecision = value;
    }

    @action
    setPreFillDecisionFields(fields: string[]): void {
        this._preFillDecisionFields = fields;
    }

    @action
    setLockViewForManager(value: boolean): void {
        this._lockViewForManager = value;
    }

    @action
    setMultiCountry(multiCountry: boolean): void {
        this._multiCountry = multiCountry;
    }

    private useTccField(tccField: string): boolean {
        return !this._excludedTccFields.has(tccField);
    }

    private usedInCompSummary(field: string): boolean {
        return this._useCompSummaryFields
            && !this._excludedCompSummaryFields.has(field);
    }
}